import { Modal, Button, Badge, Spinner, Card, ListGroup, Alert } from 'react-bootstrap';
import { 
  FaFileAlt, 
  FaCalendarAlt, 
  FaUser, 
  FaExchangeAlt, 
  FaMoneyBillWave, 
  FaChartBar,
  FaTag,
  FaDatabase,
  FaSyncAlt,
  FaFilePdf,
  FaClock,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { useState, useEffect } from 'react';
import './order-details-modal.css';
import ValidationStatusBadge from '../../../ValidationStatusBadge';
import { getOrderDocuments, openOrderDocument } from '../../../service/api/Documents/orderDocumentService';
import { toast } from 'sonner';
import useSelectedDomain from '../../../service/Hooks/useSelectedDomain';
import { formatCurrencys } from '../../../service/utils/facturationUtils';
import { formatDate, formatNumber } from '../../../service/utils/formatters';

// Composant pour afficher un document individuel
const DocumentItem = ({ document }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  // Icônes selon le type de document
  const getDocumentIcon = () => {
    const type = document.type?.toLowerCase();
    const fileName = document.fileName?.toLowerCase();
    
    if (type === 'pdf' || fileName?.includes('.pdf')) {
      return <FaFilePdf className="text-danger me-2" size={18} />;
    } else if (type === 'image' || /\.(jpg|jpeg|png|gif)$/i.test(fileName)) {
      return <FaFilePdf className="text-success me-2" size={18} />;
    } else {
      return <FaFileAlt className="text-primary me-2" size={18} />;
    }
  };

  // Calcul du temps restant
  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = new Date();
      const expiry = new Date(document.expiryTime);
      const diffMs = expiry - now;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins <= 0) {
        setIsExpired(true);
        setTimeRemaining('Expiré');
        return;
      }
      
      setIsExpired(false);
      if (diffMins < 60) {
        setTimeRemaining(`${diffMins} min`);
      } else {
        const diffHours = Math.floor(diffMins / 60);
        setTimeRemaining(`${diffHours} h`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [document.expiryTime]);

  const handleViewDocument = async () => {
    if (isExpired) {
      toast.error('Le lien a expiré. Veuillez rafraîchir la page.');
      return;
    }

    setIsLoading(true);
    try {
      await openOrderDocument(document.url, document.fileName);
      toast.success('Document ouvert dans un nouvel onglet');
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du document');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <ListGroup.Item className="document-item">
      <div className="d-flex justify-content-between align-items-start">
        <div className="d-flex align-items-start flex-grow-1 me-3">
          {getDocumentIcon()}
          <div className="flex-grow-1">
            <div className="fw-semibold text-dark">{document.name}</div>
            <small className="text-muted d-block">
              {document.fileName}
            </small>
            <div className="mt-1">
              <Badge 
                bg={isExpired ? "danger" : "light"} 
                text={isExpired ? "white" : "dark"}
                className="me-2"
              >
                <FaClock className="me-1" size={12} />
                {timeRemaining}
              </Badge>
              <Badge bg="outline-secondary" className="text-capitalize">
                {document.type?.toLowerCase()}
              </Badge>
            </div>
          </div>
        </div>
        
        <div className="d-flex flex-column gap-2">
          <Button
            variant={isExpired ? "outline-danger" : "outline-primary"}
            size="sm"
            onClick={handleViewDocument}
            disabled={isLoading || isExpired}
            className="d-flex align-items-center"
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FaExternalLinkAlt className="me-1" />
            )}
            Ouvrir
          </Button>
        </div>
      </div>
    </ListGroup.Item>
  );
};

// Composant pour la section Documents
const OrderDocumentsSection = ({ order, domainId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const selectedDomain = useSelectedDomain();
 

  const fetchDocuments = async () => {
    if (!order?.id || !selectedDomain.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const docs = await getOrderDocuments(order.id, selectedDomain?.id);
      setDocuments(docs);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des documents');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (order?.id && domainId) {
      fetchDocuments();
    }
  }, [order?.id, domainId]);

  const refreshDocuments = () => {
    fetchDocuments();
    toast.info('Rafraîchissement des liens documents');
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" />
          <p className="mt-2 mb-0 text-muted small">Chargement des documents...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-transparent border-bottom-0 d-flex justify-content-between align-items-center">
        <h5 className="mb-0 d-flex align-items-center">
          <FaFilePdf className="text-primary me-2" />
          Documents de l'ordre
        </h5>
        <div>
          <Badge bg="light" text="dark" className="me-2">
            {documents.length} document(s)
          </Badge>
          <Button variant="outline-primary" size="sm" onClick={refreshDocuments}>
            <FaSyncAlt className="me-1" />
            Rafraîchir
          </Button>
        </div>
      </Card.Header>
      
      <Card.Body className="p-0">
        {error ? (
          <Alert variant="warning" className="m-3 mb-0">
            <div className="d-flex justify-content-between align-items-center">
              <span className="small">{error}</span>
              <Button variant="outline-warning" size="sm" onClick={fetchDocuments}>
                Réessayer
              </Button>
            </div>
          </Alert>
        ) : documents.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <FaFilePdf size={32} className="mb-2 opacity-25" />
            <p className="mb-0 small">Aucun document disponible pour cet ordre</p>
          </div>
        ) : (
          <ListGroup variant="flush">
            {documents.map((doc, index) => (
              <DocumentItem 
                key={index} 
                document={doc}
              />
            ))}
          </ListGroup>
        )}
        
        {documents.length > 0 && (
          <div className="p-3 bg-light border-top">
            <div className="row align-items-center">
              <div className="col">
                <small className="text-muted">
                  <FaClock className="me-1" />
                  Les liens expirent automatiquement après 30 minutes pour des raisons de sécurité.
                </small>
              </div>
              <div className="col-auto">
                <small className="text-muted">
                  Dernière mise à jour : {new Date().toLocaleTimeString()}
                </small>
              </div>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const OrderDetailsModal = ({ show, onHide, order, loading = false, domainId }) => {

  const getStatusVariant = (status) => {
    const variants = {
      'Completed': 'success bg-opacity-10 border-opacity-10 border border-success text-success',
      'Pending': 'warning bg-opacity-10 border-opacity-10 border border-warning text-warning',
      'Canceled': 'secondary bg-opacity-10 border-opacity-10 border border-secondary text-secondary',
      'Partial': 'info bg-opacity-10 border-opacity-10 border border-info text-info',
      'Echec Completed': 'danger bg-opacity-10 border-opacity-10 border border-danger text-danger',
      'Approved': 'success bg-opacity-10 border-opacity-10 border border-success text-success',
      'Rejected': 'danger bg-opacity-10 border-opacity-10 border border-danger text-danger'
    };
    return variants[status] || 'secondary';
  };

  const getMarketLabel = (marketName) => {
    const markets = {
      'Public': 'Coté',
      'Private': 'Non coté',
      'Monetary': 'Monétaire'
    };
    return markets[marketName] || marketName;
  };

  const getSideVariant = (side) => {
    return side === 'Buy' ? 'bg-success bg-opacity-10 border-opacity-10 border border-success text-success' : side === 'Souscription' ?  'bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10' : 'danger bg-opacity-10 border-opacity-10 border-danger text-danger';
  };

  if (loading) {
    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Chargement des détails...</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Récupération des informations de l'ordre</p>
        </Modal.Body>
      </Modal>
    );
  }

  if (!order) {
    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Détails non disponibles</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-5">
          <FaFileAlt size={48} className="text-muted mb-3" />
          <p>Les détails de cet ordre ne sont pas disponibles.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="xl" className="order-details-modal" scrollable>
      <Modal.Header closeButton className="border-bottom-0 bg-light sticky-top">
        <div className="d-flex align-items-center w-100">
          <FaFileAlt className="text-primary me-3" size={24} />
          <div className="flex-grow-1">
            <Modal.Title className="h4 mb-1 text-dark">
              Détail Ordre #{order.id}
            </Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={getStatusVariant(order.status)} className="fs-6">
                {order.status}
              </Badge>
              <Badge bg="outline-primary" text="dark" className="fs-6">
                {getMarketLabel(order.targetMarketName)}
              </Badge>
              <Badge bg={getSideVariant(order.side)} className="fs-6">
                {order.side === 'Buy'
                  ? 'Achat'
                  : order.side === 'Souscription'
                    ? 'Souscription'
                    : 'Vente'}
              </Badge>

            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-0">
        <div className="container-fluid">
          {/* Section Informations Générales */}
          <div className="row g-4 mb-2 mt-1">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-bottom-0">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaDatabase className="text-primary me-2" />
                    Informations Générales
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <DetailItem 
                      icon={FaTag}
                      label="Titre"
                      value={
                        <div className="d-flex align-items-center">
                          <span className="text-danger fw-bold me-2">#{order.securityId}</span>
                          <span className="fw-semibold text-dark">{order.securityName}</span>
                        </div>
                      }
                    />
                    
                    <DetailItem 
                      icon={FaUser}
                      label="Créateur"
                      value={order.creatorName || 'N/A'}
                    />
                    
                    <DetailItem 
                      icon={FaChartBar}
                      label="Type de marché"
                      value={getMarketLabel(order.targetMarketName)}
                    />
                    
                    <DetailItem 
                      icon={FaExchangeAlt}
                      label="Type d'ordre"
                      value={
                        <Badge bg={getSideVariant(order.side)} className="fs-6">
                          {order.side === 'Buy'
                            ? 'Achat'
                            : order.side === 'Souscription'
                              ? 'Souscription'
                              : 'Vente'}
                        </Badge>

                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Détails Financiers */}
          <div className="row g-4 mb-4">
            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-transparent border-bottom-0">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaMoneyBillWave className="text-success me-2" />
                    Détails Financiers
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <DetailItem 
                      label="Prix unitaire"
                      value={`${formatCurrencys(order.price)}`}
                      className="fw-bold text-success fs-5"
                    />
                    
                    <DetailItem 
                      label="Quantité"
                      value={formatNumber(order.quantity)}
                    />
                    
                    <DetailItem 
                      label="Quantité min"
                      value={formatNumber(order.minQuantity)}
                    />
                    
                    <DetailItem 
                      label="Prix total"
                      value={`${formatCurrencys(order.price * order.quantity)}`}
                      className="fw-bold fs-5 text-primary"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-transparent border-bottom-0">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaUser className="text-info me-2" />
                    Informations Comptes
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <DetailItem 
                      label="Compte émetteur"
                      value={order.accountName || order.companyName || 'N/A'}
                    />
                    
                    <DetailItem 
                      label="Compte destinataire"
                      value={order.firstName1 || order.companyName1 || 'N/A'}
                    />
                    
                    <DetailItem 
                      label="Domaine"
                      value={order.domainName || 'N/A'}
                    />
                    
                    {order.rfi && (
                      <DetailItem 
                        label="RFI"
                        value={order.rfiName}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Métadonnées */}
          <div className="row g-4 mb-2">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-bottom-0">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaCalendarAlt className="text-warning me-2" />
                    Métadonnées
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <DetailItem 
                      icon={FaCalendarAlt}
                      label="Date de création"
                      value={formatDate(order.creationDate)}
                      className="text-muted"
                    />
                    
                    <DetailItem 
                      icon={FaSyncAlt}
                      label="Dernière modification"
                      value={formatDate(order.lastUpdateDate)}
                      className="text-muted"
                    />
                    
                    <DetailItem 
                      label="Statut de validation"
                      value={
                        <ValidationStatusBadge status={order.validationStatus} />
                      }
                    />

                    <DetailItem 
                      label="Statut de l'ordre"
                      value={
                        <Badge bg={getStatusVariant(order.status)} className="fs-6">
                          {order.status}
                        </Badge>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION DOCUMENTS - AJOUTÉE ICI */}
          <div className="row g-4">
            <div className="col-12">
              <OrderDocumentsSection 
                order={order} 
                domainId={domainId} 
              />
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 bg-light">
        <Button variant="outline-secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Composant helper pour afficher les détails individuels
const DetailItem = ({ icon: Icon, label, value, className = '' }) => (
  <div className="col-sm-6 col-lg-4">
    <div className="d-flex align-items-start">
      {Icon && <Icon className="text-muted mt-1 me-2 flex-shrink-0" size={16} />}
      <div className="flex-grow-1">
        <div className="small text-muted mb-1">{label}</div>
        <div className={`fw-semibold ${className}`}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

export default OrderDetailsModal;